import React, { Suspense, useEffect, useState } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import './assets/scss/style.scss'
import { ensureSignedInAndGetToken, checkUserAuthorization } from './services/auth'

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" data-testid="spinner"></div>
  </div>
)

const Home = React.lazy(() => import('./features/home/home'))

// Pages
const Login = React.lazy(() => import('./pages/login/Login'))
const Page404 = React.lazy(() => import('./pages/page404/Page404'))
const Page500 = React.lazy(() => import('./pages/page500/Page500'))
const Unauthorized = React.lazy(() => import('./pages/Unauthorized/Unauthorized'))

const App = () => {
  const { instance } = useMsal()
  const navigate = useNavigate()
  const [initialized, setInitialized] = useState(false)
  const [isAuthorized, setIsAuthorized] = useState(true)

  useEffect(() => {
    ;(async () => {
      try {
        await ensureSignedInAndGetToken(instance)

        // Check if user has authorization to access the application
        const hasAccess = checkUserAuthorization(instance)
        setIsAuthorized(hasAccess)

        if (!hasAccess) {
          // User is authenticated but not authorized
          navigate('/unauthorized')
        }

        setInitialized(true)
      } catch (e) {
        const msg = String(e?.message ?? e)
        if (!msg.toLowerCase().includes('redirecting')) {
          console.error('Auth initialization error:', e)
          setInitialized(true)
        }
      }
    })()
  }, [instance, navigate])

  // Google Analytics
  useEffect(() => {
    const script = document.createElement('script')
    const head = document.getElementsByTagName('head')[0]
    script.async = true
    script.type = 'text/javascript'
    script.src = 'https://www.googletagmanager.com/gtag/js?id=UA-51632266-8'
    head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    function gtag() {
      window.dataLayer.push(arguments)
    }
    gtag('js', new Date())
    gtag('config', 'UA-51632266-8', {
      anonymize_ip: true,
      allow_display_features: false,
      link_attribution: false,
    })
  }, [])

  return (
    <Suspense fallback={loading}>
      <Routes>
        <Route path="/404" element={<Page404 />} />
        <Route path="/500" element={<Page500 />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={isAuthorized ? <Login /> : <Unauthorized />} />
      </Routes>
    </Suspense>
  )
}

export default App
